// Footer Data Structure - Compatible with Contentful CMS
// This structure mimics how Contentful would return footer data for easy migration

import {
  EXTERNAL_LINKS,
  RATE_US_ON,
  SOCIAL_MEDIA_PLATFORMS,
} from '@/constants/routes';
import { getRouteByKey } from '@/constants/routes.mapping';

export interface RichText {
  raw?: string;
  json?: any;
}

export interface ContactInfo {
  type: 'address' | 'phone' | 'email' | 'hours' | 'link';
  icon?: string; // SVG path or icon identifier
  label?: string;
  value: string | string[];
  href?: string; // For clickable items
  order?: number;
}

export interface FooterLink {
  id: string;
  title: string;
  href: string;
  slug: string;
  description?: string;
  isExternal?: boolean;
  styles?: string;
  order?: number;
}

export interface FooterSection {
  id: string;
  title: string;
  slug: string;
  type: 'links' | 'contact' | 'mixed';
  description?: RichText;
  links?: FooterLink[];
  contactInfo?: ContactInfo[];
  order: number;
}

export interface RateUsPlatform {
  platform: string;
  href: string;
  icon: string; // SVG path or icon identifier
}

export interface SocialLink {
  id: string;
  platform: string;
  href: string;
  icon: string; // SVG path
  label: string;
  order?: number;
}

export interface FooterCertification {
  id: string;
  name: string;
  imageUrl?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  href?: string;
  description?: string;
  order?: number;
}

export interface FooterData {
  sections: FooterSection[];
  rateUsOn?: RateUsPlatform[];
  socialLinks?: SocialLink[];
  certifications?: FooterCertification[];
  logo?: {
    url?: string;
    alt?: string;
    href?: string;
  };
  copyrightText?: string;
  exclaimer?: string;
  legalLinks?: FooterLink[];
}

// Mock data structure matching the footer image
export const footerData: FooterData = {
  sections: [
    {
      id: 'company-info',
      title: 'LIPS Healthcare',
      slug: 'company-info',
      type: 'contact',
      order: 1,
      contactInfo: [
        {
          type: 'address',
          icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z',
          value: [
            '1st Floor, Turbine Hall B, Battersea',
            'Power Station, London SW11 8DD,',
            'United Kingdom',
          ],
          order: 1,
        },
        {
          type: 'phone',
          icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
          value: ['+44 20 7164 6114', '+44 74 4147 6100'],
          href: ['tel:+442071646114', 'https://wa.me/447441476100'],
          order: 2,
        },
        {
          type: 'email',
          icon: 'M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
          value: 'info@lips.org.uk',
          href: 'mailto:info@lips.org.uk',
          order: 3,
        },
        {
          type: 'hours',
          icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
          // label: 'Opening Hours',
          value: [
            'Mon - Fri: 9:00 AM - 9:00 PM',
            'Sat: 10:00 AM - 7:00 PM',
            'Sun: 12:00 PM - 6:00 PM',
          ],
          order: 4,
        },
      ],
    },
    {
      id: 'pharmacy',
      title: 'Pharmacy',
      slug: 'pharmacy',
      type: 'contact',
      order: 2,
      contactInfo: [
        {
          type: 'phone',
          icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
          value: '+44 20 7870 8017',
          href: 'tel:+442078708017',
          order: 1,
        },
        {
          type: 'email',
          icon: 'M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
          value: 'pharmacy.bps@lips.org.uk',
          href: 'mailto:pharmacy.bps@lips.org.uk',
          order: 2,
        },
        {
          type: 'hours',
          icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
          value: [
            'Mon - Fri: 9:00 AM - 9:00 PM',
            'Sat: 10:00 AM - 9:00 PM',
            'Sun: 12:00 PM - 6:00 PM',
          ],
          order: 3,
        },
        {
          type: 'link',
          icon: 'M3.01738 0.0468712C2.67934 0.130339 2.34129 0.322317 2.07836 0.589415C1.84883 0.818954 1.76953 0.956676 0.913979 2.66778L0 4.49574V5.53492C0 6.93302 0.0834684 7.34201 0.504984 7.98889C0.671921 8.24347 1.21029 8.79436 1.29376 8.79436C1.32297 8.79436 1.33549 10.5305 1.33549 13.7357V18.677L0.880592 18.6937C0.496637 18.7021 0.404822 18.7188 0.308833 18.7939C0.0876419 18.9566 0.0208671 19.0902 0.0208671 19.3531C0.0208671 19.616 0.0876419 19.7496 0.308833 19.9124L0.421516 20H10.0162H19.6109L19.7236 19.9124C19.9448 19.7496 20.0116 19.616 20.0116 19.3531C20.0116 19.0902 19.9448 18.9566 19.7236 18.7939C19.6276 18.7188 19.5358 18.7021 19.1518 18.6937L18.6969 18.677V13.7357C18.6969 10.5305 18.7094 8.79436 18.7387 8.79436C18.8221 8.79436 19.3605 8.24347 19.5274 7.98889C19.949 7.34201 20.0324 6.93302 20.0324 5.53909V4.50408L19.1518 2.73455C18.6677 1.76632 18.2003 0.873207 18.1085 0.756351C17.9123 0.497601 17.5367 0.226328 17.207 0.101126L16.965 0.00930977L10.0997 0.000963211C4.69927 -0.00321007 3.18849 0.00513649 3.01738 0.0468712ZM16.7646 1.36984C16.8272 1.39489 16.9274 1.45331 16.9817 1.5034C17.0359 1.55348 17.3489 2.13358 17.6786 2.79298L18.2796 3.99493H10.0162H1.75284L2.35381 2.79298C2.68351 2.13358 2.99652 1.55348 3.05077 1.5034C3.10503 1.45331 3.19684 1.39489 3.25527 1.36984C3.40551 1.31142 16.606 1.30724 16.7646 1.36984ZM4.66589 6.02738C4.65337 6.71183 4.64919 6.73269 4.51982 7.00814C4.33618 7.39627 4.10247 7.63832 3.72269 7.82613C3.42221 7.9722 3.38464 7.98055 3.00904 7.98055C2.64595 7.98055 2.58335 7.96803 2.32877 7.847C1.94064 7.66337 1.69858 7.42965 1.51078 7.04987L1.35636 6.73269L1.33967 6.03156L1.32715 5.33042H3.00486H4.68258L4.66589 6.02738ZM9.34012 6.02738C9.3276 6.71183 9.32342 6.73269 9.19405 7.00814C9.01042 7.39627 8.77671 7.63832 8.39692 7.82613C8.09644 7.9722 8.05888 7.98055 7.68327 7.98055C7.32018 7.98055 7.25758 7.96803 7.003 7.847C6.61487 7.66337 6.37282 7.42965 6.18501 7.04987L6.03059 6.73269L6.0139 6.03156L6.00138 5.33042H7.6791H9.35681L9.34012 6.02738ZM14.0143 6.02738C14.0018 6.71183 13.9977 6.73269 13.8683 7.00814C13.6847 7.39627 13.4509 7.63832 13.0712 7.82613C12.7707 7.9722 12.7331 7.98055 12.3575 7.98055C11.9944 7.98055 11.9318 7.96803 11.6772 7.847C11.2891 7.66337 11.047 7.42965 10.8592 7.04987L10.7048 6.73269L10.6881 6.03156L10.6756 5.33042H12.3533H14.031L14.0143 6.02738ZM18.6886 6.02738C18.6761 6.71183 18.6719 6.73269 18.5425 7.00814C18.3589 7.39627 18.1252 7.63832 17.7454 7.82613C17.4449 7.9722 17.4073 7.98055 17.0317 7.98055C16.6686 7.98055 16.606 7.96803 16.3515 7.847C15.9633 7.66337 15.7213 7.42965 15.5335 7.04987L15.3791 6.73269L15.3624 6.03156L15.3498 5.33042H17.0276H18.7053L18.6886 6.02738ZM5.63412 8.51892C5.96382 8.82775 6.3436 9.05311 6.79433 9.20336C7.07395 9.29935 7.18663 9.31187 7.6791 9.31187C8.17156 9.31187 8.28424 9.29935 8.56386 9.20336C9.01459 9.05311 9.39437 8.82775 9.72825 8.51474L10.0162 8.24347L10.3042 8.51474C10.6381 8.82775 11.0178 9.05311 11.4686 9.20336C11.7482 9.29935 11.8609 9.31187 12.3533 9.31187C12.8458 9.31187 12.9585 9.29935 13.2381 9.20336C13.6888 9.05311 14.0686 8.82775 14.4025 8.51474L14.6904 8.24347L14.9993 8.53144C15.3415 8.85279 15.8256 9.12406 16.2555 9.23674C16.3932 9.27013 16.6979 9.31187 16.9357 9.32439L17.3614 9.34943V14.0153V18.6854H15.3582H13.3549V16.5653C13.3549 14.2365 13.3508 14.1864 13.1004 13.7148C12.9418 13.4227 12.5745 13.0596 12.2782 12.9094C11.8442 12.6882 11.7148 12.6756 10.0162 12.6756C8.31763 12.6756 8.18825 12.6882 7.75422 12.9094C7.4579 13.0596 7.09064 13.4227 6.93205 13.7148C6.68165 14.1864 6.67747 14.2365 6.67747 16.5653V18.6854H4.67423H2.67099V14.0195V9.35777L3.05912 9.3536C3.79781 9.34525 4.45721 9.07816 4.99559 8.569C5.17504 8.39371 5.32946 8.25599 5.33781 8.25182C5.34615 8.25182 5.4797 8.37285 5.63412 8.51892ZM11.7106 14.1196C11.7732 14.1656 11.865 14.2574 11.9109 14.32C11.9944 14.4327 11.9986 14.4869 12.0111 16.5611L12.0236 18.6854H10.0162H8.01297V16.6237C8.01297 15.2632 8.02966 14.5203 8.05888 14.441C8.08392 14.3784 8.14235 14.2782 8.19243 14.224C8.3844 14.0153 8.42614 14.0111 10.0663 14.0237C11.5395 14.032 11.6021 14.0362 11.7106 14.1196Z',
          href: EXTERNAL_LINKS.ONLINE_STORE,
          order: 4,
          value: 'Our Online Store',
        },
      ],
    },
    {
      id: 'legal-policies',
      title: 'Legal & Policies',
      slug: 'legal-policies',
      type: 'links',
      order: 3,
      links: [
        {
          id: 'privacy-policy',
          title: 'Privacy Policy',
          href:
            getRouteByKey('privacy-policy')?.marketingUrl ?? '/privacy-policy',
          slug: 'privacy-policy',
          description: 'How we handle your data',
          order: 1,
        },
        {
          id: 'cookies-policy',
          title: 'Cookies Policy',
          href: getRouteByKey('cookies-policy')?.marketingUrl ?? '/cookies',
          slug: 'cookies-policy',
          description: 'Information about cookies',
          order: 2,
        },
        {
          id: 'terms-conditions',
          title: 'Terms & Conditions',
          href:
            getRouteByKey('terms-and-conditions')?.marketingUrl ??
            '/terms-and-conditions',
          slug: 'terms-conditions',
          description: 'Terms of service',
          order: 3,
        },
        {
          id: 'complaint-policy',
          title: 'Complaint Policy',
          href:
            getRouteByKey('complaint-policy')?.marketingUrl ??
            '/complaint-policy',
          slug: 'complaint-policy',
          description: 'How to make a complaint',
          order: 4,
        },
        {
          id: 'cancellation-policy',
          title: 'Cancellation Policy',
          href:
            getRouteByKey('cancellation-policy')?.marketingUrl ??
            '/cancellation-policy',
          slug: 'cancellation-policy',
          description: 'Our cancellation policy',
          order: 5,
        },
      ],
    },
    {
      id: 'help-support',
      title: 'Help & Support',
      slug: 'help-support',
      type: 'links',
      order: 4,
      links: [
        {
          id: 'faqs',
          title: 'FAQs',
          href: '/about-lips/faqs',
          slug: 'faqs',
          description: 'Frequently asked questions about LIPS Healthcare',
          order: 0,
        },
        {
          id: 'feedback',
          title: 'Leave a Feedback',
          href: getRouteByKey('leave-feedback')?.marketingUrl ?? '/feedback',
          slug: 'feedback',
          description: 'Share your feedback',
          order: 1,
        },
      ],
    },
    {
      id: 'rate-us',
      title: 'Rate Us On',
      slug: 'rate-us',
      type: 'links',
      order: 5,
      links: RATE_US_ON.map((platform, index) => ({
        id: platform.platform.toLowerCase(),
        title: platform.platform,
        href: platform.href,
        slug: platform.platform.toLowerCase(),
        description: `Follow us on ${platform.platform}`,
        isExternal: true,
        imageUrl: platform.imageUrl,
        styles: platform.styles,
        order: index + 1,
      })),
    },
  ],
  certifications: [
    {
      id: 'care-quality-commission',
      name: 'Care Quality Commission',
      imageUrl: '/images/footer/care-quality-commission.webp',
      imageAlt: 'Regulated by the Care Quality Commission',
      imageWidth: 256,
      imageHeight: 120,
      href: EXTERNAL_LINKS.CQC,
      description: 'Regulated by the Care Quality Commission',
      order: 1,
    },
    {
      id: 'cyber-essentials',
      name: 'Cyber Essentials',
      imageUrl: '/images/footer/cyber-essentials.webp',
      imageAlt: 'Cyber Essentials certified',
      imageWidth: 101,
      imageHeight: 120,
      href: EXTERNAL_LINKS.CYBER_ESSENTIALS,
      description: 'Cyber Essentials certified',
      order: 2,
    },
  ],
  logo: {
    url: '/images/logo/lips-10-years.png',
    alt: 'LIPS Healthcare',
    href: getRouteByKey('home')?.marketingUrl ?? '/',
  },
  copyrightText: '© 2026 LIPS Healthcare. All rights reserved.',
  exclaimer:
    'LIPS BATTERSEA LIMITED, trading as LIPS Healthcare, is an Appointed Representative of Chrysalis Finance Limited, which is authorised and regulated by the Financial Conduct Authority. LIPS BATTERSEA LIMITED is a credit broker, not a lender. The provider of a payment scheme which is not offered through or by Chrysalis Finance Limited may not be so authorised and regulated.',
  legalLinks: [
    {
      id: 'privacy',
      title: 'Privacy',
      href: getRouteByKey('privacy-policy')?.marketingUrl ?? '/privacy-policy',
      slug: 'privacy',
      order: 1,
    },
    {
      id: 'terms',
      title: 'Terms',
      href:
        getRouteByKey('terms-and-conditions')?.marketingUrl ??
        '/terms-and-conditions',
      slug: 'terms',
      order: 2,
    },
    {
      id: 'cookies',
      title: 'Cookies',
      href: getRouteByKey('cookies-policy')?.marketingUrl ?? '/cookies',
      slug: 'cookies',
      order: 3,
    },
  ],
  rateUsOn: RATE_US_ON.map((platform, index) => ({
    id: platform.platform.toLowerCase(),
    platform: platform.platform,
    href: platform.href,
    icon: platform.icon,
    label: `Follow us on ${platform.platform}`,
    order: index + 1,
  })),
  socialLinks: SOCIAL_MEDIA_PLATFORMS.map((platform, index) => ({
    id: platform.platform.toLowerCase(),
    platform: platform.platform,
    href: platform.href,
    icon: platform.icon,
    label: `Follow us on ${platform.platform}`,
    order: index + 1,
  })),
};

// Helper functions for easy migration to Contentful
export const getFooterSections = (): FooterSection[] => {
  return footerData.sections.sort((a, b) => a.order - b.order);
};

export const getFooterLinks = (sectionId: string): FooterLink[] => {
  const section = footerData.sections.find(
    (section) => section.id === sectionId,
  );
  return section?.links?.sort((a, b) => (a.order || 0) - (b.order || 0)) || [];
};

export const getContactInfo = (sectionId: string): ContactInfo[] => {
  const section = footerData.sections.find(
    (section) => section.id === sectionId,
  );
  return (
    section?.contactInfo?.sort((a, b) => (a.order || 0) - (b.order || 0)) || []
  );
};

export const getCertifications = (): FooterCertification[] => {
  return (
    footerData.certifications?.sort(
      (a, b) => (a.order || 0) - (b.order || 0),
    ) || []
  );
};

// Helper to format contact info values
export const formatContactValue = (contactInfo: ContactInfo): string[] => {
  return Array.isArray(contactInfo.value)
    ? contactInfo.value
    : [contactInfo.value];
};

// Helper to get contact href
export const getContactHref = (
  contactInfo: ContactInfo,
): string | string[] | undefined => {
  return contactInfo.href;
};
